import axios from 'axios'
import Cookies from 'js-cookie'

// Create a configured axios instance like in fighter.js
const api = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

export default {
  namespaced: true,
  state: {
    notifications: [],
    count: 0,
    loading: false
  },
  mutations: {
    SET_NOTIFICATIONS(state, notifications) {
      state.notifications = notifications
    },
    SET_COUNT(state, count) {
      state.count = count
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    REMOVE_NOTIFICATION(state, notificationId) {
      state.notifications = state.notifications.filter(n => n.id !== notificationId)
      state.count = Math.max(0, state.count - 1)
    }
  },
  actions: {
    async fetchRecentNotifications({ commit, rootState }) {
      try {
        commit('SET_LOADING', true)
        // Get player ID safely from playerData if available
        const playerId = rootState.player.playerData?.id
        
        // Only proceed if we have a player ID
        if (!playerId) {
          console.log('Player data not loaded yet, skipping notification fetch')
          return []
        }
        
        const response = await api.get(`/api/notifications/recent/${playerId}`, {
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}`
          }
        })
        
        commit('SET_NOTIFICATIONS', response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching notifications:', error)
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchAllNotifications({ commit, rootState }) {
      try {
        commit('SET_LOADING', true)
        // Get player ID safely from playerData
        const playerId = rootState.player.playerData?.id
        
        if (!playerId) {
          console.log('Player data not loaded yet, skipping notification fetch')
          return []
        }
        
        const response = await api.get(`/api/notifications/all/${playerId}`, {
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}`
          }
        })
        
        commit('SET_NOTIFICATIONS', response.data)
        return response.data
      } catch (error) {
        console.error('Error fetching all notifications:', error)
        const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to fetch notifications'
        console.error(errorMessage)
        return []
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async getNotificationCount({ commit, rootState }) {
      try {
        // Get player ID safely from playerData
        const playerId = rootState.player.playerData?.id
        
        if (!playerId) {
          console.log('Player data not loaded yet, skipping notification count fetch')
          return 0
        }
        
        const response = await api.get(`/api/notifications/count/${playerId}`, {
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}`
          }
        })
        
        commit('SET_COUNT', response.data.count)
        return response.data.count
      } catch (error) {
        console.error('Error fetching notification count:', error)
        return 0
      }
    },
    
    async deleteNotification({ commit }, notificationId) {
      try {
        await api.delete(`/api/notifications/${notificationId}`, {
          headers: { 
            Authorization: `Bearer ${Cookies.get('jwt')}`
          }
        })
        
        commit('REMOVE_NOTIFICATION', notificationId)
        return true
      } catch (error) {
        console.error('Error deleting notification:', error)
        const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to delete notification'
        console.error(errorMessage)
        return false
      }
    },
    
    startPolling({ dispatch, rootState }) {
      // Poll every 5 minutes (300000 ms)
      const intervalId = setInterval(() => {
        if (rootState.player.playerData?.id) {
          dispatch('getNotificationCount')
        }
      }, 300000)
      
      // Store the interval ID on the window object to be able to clear it later if needed
      window.notificationPollingInterval = intervalId
    },
    
    stopPolling() {
      if (window.notificationPollingInterval) {
        clearInterval(window.notificationPollingInterval)
      }
    }
  },
  getters: {
    getNotifications: state => state.notifications,
    getCount: state => state.count,
    isLoading: state => state.loading
  }
}
